import apiService from './api/apiService'
import { accessTokenKey } from '../constants'
import { GraphQLClient } from 'graphql-request'
import 'abort-controller/polyfill'
import { parse } from 'query-string'

const abortControllers: { [methodName: string]: AbortController } = {}

export type GraphQlError = {
  data: any
  errors: Array<{ message: string }>
  status: number
}
export type GraphqlRequestProps = { query: string; methodName?: string }

type QueueItem = {
  request: GraphqlRequestProps
  resolve: (value: any | GraphQlError) => void
  reject: (error: any) => void
}

const requestQueue: QueueItem[] = []
let pendingRequests = 0
const MAX_CONCURRENT_REQUESTS = 5
let isProcessing = false
const pendingResolvers: Array<() => void> = []

const graphqlService = {
  async execute(query: string, methodName?: string): Promise<any | GraphQlError> {
    if (methodName) {
      abortControllers[methodName] = new AbortController()
    }
    const { requestId: pdfRequestId } = parse(window.location.search) as {
      requestId: string
    }

    const graphQLClient = new GraphQLClient('/api/graphql', {
      headers: {
        [accessTokenKey]: window.localStorage.getItem(accessTokenKey) || '',
        'oidc-redirect': 'false'
      }
    })
    try {
      const response = await graphQLClient.request({
        document: query,
        ...(methodName ? { signal: abortControllers[methodName].signal as any } : {}) // types is not presented yet
      })
      return response
    } catch (error: any) {
      if (error?.response && error.response.status === 401) {
        apiService.logout()
      }

      const errorData = JSON.parse(JSON.stringify(error))
      if (pdfRequestId)
        await apiService.postPdfDownloadError(
          pdfRequestId,
          {
            url: '/api/graphql',
            body: { query },
            statusCode: errorData.status,
            response: errorData.errors,
            method: 'POST'
          },
          'failed'
        )

      throw errorData.response
    }
  },

  abort(methodName: string) {
    abortControllers[methodName]?.abort()
  },

  addToQueue(query: string, methodName?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      requestQueue.push({ request: { query, methodName }, resolve, reject })
      setTimeout(() => this.processQueue(), 0) // to not duplicate requests that are coming in the same tick
    })
  },

  async processQueue() {
    if (isProcessing) return
    isProcessing = true
    try {
      while (requestQueue.length > 0) {
        while (pendingRequests < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
          const item = requestQueue.shift()
          if (!item) continue

          pendingRequests++
          const executeRequest = async () => {
            try {
              const result = await this.execute(item.request.query, item.request.methodName)
              item.resolve(result)
            } catch (error) {
              item.reject(error)
            } finally {
              pendingRequests--
              if (pendingResolvers.length > 0) {
                const resolver = pendingResolvers.shift()
                resolver && resolver()
              }
            }
          }
          executeRequest()
        }
        if (requestQueue.length > 0 && pendingRequests >= MAX_CONCURRENT_REQUESTS) {
          await new Promise<void>((resolve) => {
            pendingResolvers.push(resolve)
          })
        }
      }
    } finally {
      isProcessing = false
    }
  }
}

export default graphqlService
